import { useEffect, useMemo, useState } from "react";
import { Image as ImageIcon, Package, Pencil, RefreshCw, Search, Trash2 } from "lucide-react";
import { api, ApiError } from "../api";
import type { ProductCatalogItem, ProductCategoryItem } from "../types";
import { unitLabels } from "../types";
import { Alert, EmptyState, formatNumber, Loading, Modal, PageHeader } from "../components/Ui";

type SortKey = "name" | "code" | "purchasePrice" | "salePrice" | "totalQuantity" | "availableQuantity";

export function CatalogPage() {
  const [items, setItems] = useState<ProductCatalogItem[]>([]);
  const [categories, setCategories] = useState<ProductCategoryItem[]>([]);
  const [partner, setPartner] = useState<Record<string, { partnerPrice: number | null; isPartnerVisible: boolean }>>({});
  const [saving, setSaving] = useState<string>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  
  const [search, setSearch] = useState("");
  const [inStockOnly, setInStockOnly] = useState(false);

  const [sortKey, setSortKey] = useState<SortKey>("name");
  const [sortDesc, setSortDesc] = useState(false);
  const [editing, setEditing] = useState<ProductCatalogItem|null>(null);
  const [editForm, setEditForm] = useState({name:"",salePrice:0,isStorefrontPublished:false,categoryId:"",brand:"",description:"",specifications:""});
  const [editBusy,setEditBusy]=useState(false);

  async function load() {
    setLoading(true);
    setError("");
    try {
      const [catalog, publications, productCategories] = await Promise.all([api.catalogProducts(), api.partnerCatalog(), api.catalogCategories()]);
      setItems(catalog);
      setCategories(productCategories);
      setPartner(Object.fromEntries(publications.map(x => [x.id, { partnerPrice: x.partnerPrice, isPartnerVisible: x.isPartnerVisible }])));
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Не удалось загрузить каталог товаров");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { void load(); }, []);

  const visible = useMemo(() => {
    let result = items;
    
    if (inStockOnly) {
      result = result.filter(x => x.totalQuantity > 0);
    }

    const term = search.trim().toLowerCase();
    if (term) {
      result = result.filter(x => `${x.code} ${x.name}`.toLowerCase().includes(term));
    }

    result.sort((a, b) => {
      let valA: any = a[sortKey as keyof ProductCatalogItem] ?? "";
      let valB: any = b[sortKey as keyof ProductCatalogItem] ?? "";
      
      if (typeof valA === "string") valA = valA.toLowerCase();
      if (typeof valB === "string") valB = valB.toLowerCase();

      if (valA < valB) return sortDesc ? 1 : -1;
      if (valA > valB) return sortDesc ? -1 : 1;
      return 0;
    });

    return result;
  }, [items, search, inStockOnly, sortKey, sortDesc]);

  function handleSort(key: SortKey) {
    if (sortKey === key) {
      setSortDesc(!sortDesc);
    } else {
      setSortKey(key);
      setSortDesc(key === "totalQuantity" || key === "availableQuantity" || key === "purchasePrice" || key === "salePrice");
    }
  }

  function renderSortIcon(key: SortKey) {
    if (sortKey !== key) return null;
    return sortDesc ? " ↓" : " ↑";
  }

  async function savePartner(id: string) {
    const value = partner[id] ?? { partnerPrice: null, isPartnerVisible: false };
    setSaving(id); setError("");
    try { await api.updatePartnerCatalog(id, value); }
    catch (e) { setError(e instanceof ApiError ? e.message : "Не удалось сохранить партнёрскую цену"); }
    finally { setSaving(undefined); }
  }

  function openEdit(item:ProductCatalogItem) {
    setEditing(item);
    setEditForm({name:item.name,salePrice:item.salePrice,isStorefrontPublished:item.isStorefrontPublished,categoryId:item.categoryId??"",brand:item.brand,description:item.description,specifications:Object.entries(item.specifications).map(([key,value])=>`${key}: ${value}`).join("\n")});
  }

  function specifications() {
    return Object.fromEntries(editForm.specifications.split("\n").map(line=>line.trim()).filter(Boolean).map(line=>{const index=line.indexOf(":");return index<0?[line,""]:[line.slice(0,index).trim(),line.slice(index+1).trim()];}));
  }

  async function saveProduct(event:React.FormEvent) {
    event.preventDefault(); if(!editing)return; setEditBusy(true);setError("");
    try { await api.updateCatalogProduct(editing.id,{...editForm,categoryId:editForm.categoryId||null,specifications:specifications()}); await load(); setEditing(null); }
    catch(e){setError(e instanceof ApiError?e.message:"Не удалось сохранить товар");}
    finally{setEditBusy(false);}
  }

  async function uploadImages(files:FileList|null) {
    if(!editing||!files?.length)return;
    if(editing.images.length+files.length>10){setError("Для товара разрешено не более 10 изображений.");return;}
    if([...files].some(file=>file.size>10*1024*1024)){setError("Размер каждого изображения должен быть не больше 10 МБ.");return;}
    setEditBusy(true);setError("");
    try { for(const file of [...files]) await api.uploadCatalogImage(editing.id,file,editing.name); const catalog=await api.catalogProducts();setItems(catalog);const next=catalog.find(x=>x.id===editing.id)??null;setEditing(next); }
    catch(e){setError(e instanceof ApiError?e.message:"Не удалось загрузить изображения");}
    finally{setEditBusy(false);}
  }

  async function deleteImage(imageId:string) {
    if(!editing)return;setEditBusy(true);setError("");
    try{await api.deleteCatalogImage(editing.id,imageId);const catalog=await api.catalogProducts();setItems(catalog);setEditing(catalog.find(x=>x.id===editing.id)??null);}
    catch(e){setError(e instanceof ApiError?e.message:"Не удалось удалить изображение");}
    finally{setEditBusy(false);}
  }

  return <>
    <PageHeader 
      eyebrow="Справочники" 
      title="Каталог товаров" 
      text="Внутренний каталог WMS. Здесь же выбираются материалы и цены, которые разрешено показывать партнёрам."
      action={<div style={{display:"flex",gap:10}}><a className="button button--primary" href={import.meta.env.VITE_STOREFRONT_URL || (window.location.hostname === "wms.nord-group.kg" ? "https://nord-group.kg/catalog/" : "/shop/")} target="_blank" rel="noreferrer">Открыть интернет-магазин</a><button className="button button--secondary" onClick={load}><RefreshCw size={17} />Обновить</button></div>}
    />
    
    {error && <Alert>{error}</Alert>}
    
    <div className="toolbar" style={{ display: "flex", gap: "1rem", flexWrap: "wrap", alignItems: "center" }}>
      <div className="search" style={{ flex: 1, minWidth: "200px" }}>
        <Search />
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Код или название товара" />
      </div>
      
      <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
        <label style={{ display: "flex", alignItems: "center", gap: "0.5rem", cursor: "pointer" }}>
          <input type="checkbox" checked={inStockOnly} onChange={e => setInStockOnly(e.target.checked)} />
          Только в наличии
        </label>
      </div>
    </div>

    <section className="panel panel--flush">
      {loading ? <Loading /> : visible.length === 0 ? (
        <EmptyState icon={<Package />} title="Нет товаров" text="По заданным критериям ничего не найдено." />
      ) : (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th onClick={() => handleSort("code")} style={{ cursor: "pointer", width: "15%" }}>
                  Код {renderSortIcon("code")}
                </th>
                <th onClick={() => handleSort("name")} style={{ cursor: "pointer", width: "35%" }}>
                  Наименование {renderSortIcon("name")}
                </th>
                <th onClick={() => handleSort("purchasePrice")} style={{ cursor: "pointer", textAlign: "right", width: "10%" }}>
                  Цена закупки {renderSortIcon("purchasePrice")}
                </th>
                <th onClick={() => handleSort("salePrice")} style={{ cursor: "pointer", textAlign: "right", width: "10%" }}>
                  Цена розницы {renderSortIcon("salePrice")}
                </th>
                <th onClick={() => handleSort("totalQuantity")} style={{ cursor: "pointer", textAlign: "right", width: "15%" }}>
                  Всего на складе {renderSortIcon("totalQuantity")}
                </th>
                <th onClick={() => handleSort("availableQuantity")} style={{ cursor: "pointer", textAlign: "right", width: "15%" }}>
                  Доступно {renderSortIcon("availableQuantity")}
                </th>
                <th>Партнёрская цена</th>
                <th>Показывать партнёрам</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {visible.map(item => (
                <tr key={item.id}>
                  <td className="mono">{item.code}</td>
                  <td><b>{item.name}</b><small style={{display:"block",color:"var(--muted)",marginTop:4}}>{item.isStorefrontPublished?"Опубликован в магазине":"Не опубликован"}{item.category?` · ${item.category}`:""}</small></td>
                  <td style={{ textAlign: "right" }}>{formatNumber(item.purchasePrice)} ₽</td>
                  <td style={{ textAlign: "right" }}>{formatNumber(item.salePrice)} ₽</td>
                  <td style={{ textAlign: "right" }}>
                    <b>{formatNumber(item.totalQuantity)}</b> {unitLabels[item.unit] ?? item.unit}
                  </td>
                  <td style={{ textAlign: "right" }}>
                    <b>{formatNumber(item.availableQuantity)}</b> {unitLabels[item.unit] ?? item.unit}
                  </td>
                  <td><input aria-label={`Партнёрская цена ${item.name}`} type="number" min="0" step="0.01" value={partner[item.id]?.partnerPrice ?? ""} onChange={e => setPartner({ ...partner, [item.id]: { ...(partner[item.id] ?? { isPartnerVisible: false }), partnerPrice: e.target.value === "" ? null : Number(e.target.value) } })} /></td>
                  <td><input aria-label={`Публикация ${item.name}`} type="checkbox" checked={partner[item.id]?.isPartnerVisible ?? false} onChange={e => setPartner({ ...partner, [item.id]: { ...(partner[item.id] ?? { partnerPrice: null }), isPartnerVisible: e.target.checked } })} /></td>
                  <td><div style={{display:"flex",gap:8}}><button className="button button--secondary" onClick={()=>openEdit(item)}><Pencil size={16}/>Редактировать</button><button className="button button--secondary" disabled={saving === item.id} onClick={() => void savePartner(item.id)}>{saving === item.id ? "Сохраняем…" : "Сохранить партнёру"}</button></div></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
    {editing&&<Modal wide title="Редактирование товара" onClose={()=>setEditing(null)}><form className="modal-form" onSubmit={saveProduct}>
      <div className="form-grid"><label className="field"><span>Название</span><input value={editForm.name} onChange={e=>setEditForm({...editForm,name:e.target.value})} required/></label><label className="field"><span>Цена продажи, сом</span><input type="number" min="0" step="0.01" value={editForm.salePrice} onChange={e=>setEditForm({...editForm,salePrice:Number(e.target.value)})}/></label><label className="field"><span>Категория</span><select value={editForm.categoryId} onChange={e=>setEditForm({...editForm,categoryId:e.target.value})}><option value="">Без категории</option>{categories.map(category=><option key={category.id} value={category.id}>{category.name}{category.isActive?"":" (скрыта)"}</option>)}</select><small>Категории создаются на отдельной странице «Категории».</small></label><label className="field"><span>Бренд</span><input value={editForm.brand} onChange={e=>setEditForm({...editForm,brand:e.target.value})}/></label></div>
      <label className="field"><span>Описание товара</span><textarea rows={5} value={editForm.description} onChange={e=>setEditForm({...editForm,description:e.target.value})}/></label>
      <label className="field"><span>Характеристики</span><textarea rows={6} value={editForm.specifications} onChange={e=>setEditForm({...editForm,specifications:e.target.value})} placeholder={'Цвет: Белый\nДлина: 6 м\nСистема: Grain'}/><small>Одна характеристика в строке, формат: Название: Значение</small></label>
      <label style={{display:"flex",alignItems:"center",gap:10,margin:"18px 0"}}><input type="checkbox" checked={editForm.isStorefrontPublished} onChange={e=>setEditForm({...editForm,isStorefrontPublished:e.target.checked})}/><b>Опубликовать в интернет-магазине</b></label>
      <section style={{marginTop:20}}><h3 style={{margin:"0 0 6px"}}>Изображения ({editing.images.length}/10)</h3><p style={{color:"var(--muted)",margin:"0 0 12px"}}>До 10 изображений, каждое не больше 10 МБ.</p><label className="button button--secondary" style={{display:"inline-flex",cursor:"pointer"}}><ImageIcon size={17}/>Загрузить изображения<input hidden type="file" accept="image/*" multiple disabled={editBusy||editing.images.length>=10} onChange={e=>void uploadImages(e.target.files)}/></label><div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(120px,1fr))",gap:12,marginTop:14}}>{editing.images.map(image=><figure key={image.id} style={{margin:0,position:"relative"}}><img src={`/api/v1/store/images/${image.id}`} alt={image.altText} style={{width:"100%",height:100,objectFit:"cover",borderRadius:10,border:"1px solid var(--line)"}}/><button type="button" className="icon-button" style={{position:"absolute",right:5,top:5,background:"white"}} onClick={()=>void deleteImage(image.id)} aria-label="Удалить изображение"><Trash2 size={16}/></button></figure>)}</div></section>
      {error&&<Alert>{error}</Alert>}<div className="form-actions"><button type="button" className="button button--secondary" onClick={()=>setEditing(null)}>Отмена</button><button className="button button--primary" disabled={editBusy}>{editBusy?"Сохраняем…":"Сохранить товар"}</button></div>
    </form></Modal>}
  </>;
}
